import { useEffect, useMemo, useState } from 'react';
import { renderPartCrops, shrinkDataUrl } from './partCrop';
import { usePartsRun } from './partsRun';
import { useCataloguedParts } from './photographedParts';
import { usePartModelJobs } from './partModels';
import type { PublishPart } from './publishApi';

// THE PARTS LIST THE PUBLIC PAGE OPENS ON: its Tools screen, then its Parts screen, before the
// first step. Every part the manual lists, named in both languages, with its count, its
// drawing cut out of the manual — small, since a card on a phone is a few hundred pixels wide —
// and its 3D model, which the page shows in the drawing's place (Louis 2026-10-23).
// Empty until the drawings are cut: a publish pressed before then goes out without the list.
export function usePublishParts(docId: string, file: Blob): PublishPart[] {
  const { items } = usePartsRun(docId);
  const { parts: catalogued } = useCataloguedParts(docId);
  const { modelOf } = usePartModelJobs();
  const [images, setImages] = useState<{ of: typeof items; urls: string[] } | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const crops = await renderPartCrops(file, items);
      const urls = await Promise.all(crops.map((c) => (c ? shrinkDataUrl(c, 320) : Promise.resolve(''))));
      if (!cancelled) setImages({ of: items, urls });
    })();
    return () => { cancelled = true; };
  }, [file, items]);
  return useMemo(() => {
    if (images?.of !== items) return [];
    // The same join as everywhere else, by the part itself (see useDocTools).
    const itemOf = new Map<unknown, number>(catalogued.map((c) => [c.part, c.itemId]));
    return items.map((p, i) => ({
      nameEn: p.nameEn || p.name || p.nameFr || '',
      nameFr: p.nameFr || p.name || p.nameEn || '',
      qty: p.qty,
      letter: (p.letter || '').trim(),
      tool: p.tool,
      image: images.urls[i] ?? '',
      supplied: p.supplied !== false,
      job: modelOf.get(itemOf.get(p) ?? -1)?.id ?? null,
    }));
  }, [images, items, catalogued, modelOf]);
}
